import React, { useState } from "react";
import { getToken } from "../../../../../utils/token";
import instance from "../../../../../axios/instance";
import { Link, useLoaderData, useNavigate, useRevalidator } from "react-router-dom";
import ConsultationChart from "../../compomnents/ConsultationChart";
import { Activity, Shop, Timer1, User } from "iconsax-react";
import { Tag, X } from "lucide-react";
import { toast } from "react-toastify";

const top4Treatments = (consultations) => {
  const treatmentDetails = consultations.reduce((acc, consultation) => {
    if (!consultation.treatment) return acc;
    const { id, title } = consultation.treatment;
    if (!acc[id]) {
      acc[id] = { id, title, count: 0 };
    }
    acc[id].count++;
    return acc;
  }, {});

  const sortedTreatments = Object.values(treatmentDetails).sort(
    (a, b) => b.count - a.count
  );

  return sortedTreatments.slice(0, 6);
};

export const consultationLoader = async () => {
  const token = getToken();
  const res = await instance.get("/doctor", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const isAdmin = res.data.access === "admin";
  let consultations = [];
  if (isAdmin) {
    const consultationsRes = await instance.get(
      "/telemedicine/facility/consultation/all",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    consultations = consultationsRes.data;
  } else {
    const consultationsRes = await instance.get(
      "/telemedicine/doctor/consultation",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    consultations = consultationsRes.data;
  }

  return {
    doctor: res.data,
    consultations,
    sortedTreatments: top4Treatments(consultations),
  };
};

const ConsultationPage = () => {
  const { doctor, consultations, sortedTreatments } = useLoaderData();
  const isAdmin = doctor.access === "admin";

  return (
    <>
      {isAdmin ? (
        <AdminConsultationPage
          consultations={consultations}
          sortedTreatments={sortedTreatments}
        />
      ) : (
        <DocConsultationPage consultations={consultations} />
      )}
    </>
  );
};

export default ConsultationPage;

const AdminConsultationPage = ({ consultations, sortedTreatments }) => {
  const navigate = useNavigate();
  return (
    <div className="">
      <h2 className="text-lg font-medium">Consultations Overview</h2>
      <ConsultationChart data={consultations} />

      <div className="grid grid-cols-2 my-6 gap-4">
        <Link
          to="/dashboard/telemedicine/consultations/all"
          className="cursor-pointer text-[#00b0ad] bg-[#00b0ad22] py-6 px-4 rounded-[6px] flex flex-col justify-center items-center gap-4"
        >
          <Activity size={36} variant="Bold" />
          <div className="font-medium text-[14px]">All Consultations</div>
        </Link>
        <Link
          to="/dashboard/telemedicine/consultations/pending"
          className="cursor-pointer text-[#00b0ad] bg-[#00b0ad22] py-6 px-4 rounded-[6px] flex flex-col justify-center items-center gap-4"
        >
          <Timer1 size={36} variant="Bold" />
          <div className="font-medium text-[14px]">Pending Consultations</div>
        </Link>
      </div>

      {consultations.length >= 1 && (
        <>
          <h2 className="text-lg font-medium mb-2">Treatments Overview</h2>
          <div>
            <ul className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
              {sortedTreatments.map((treatment, index) => (
                <li
                  key={treatment.id}
                  className="border rounded-lg px-5 py-5 flex items-center cursor-pointer"
                  onClick={() =>
                    navigate(
                      `/dashboard/settings/treatment-setup/treatments/${treatment.id}`
                    )
                  }
                >
                  <img src="/images/vial.webp" width={100} height={100} />
                  <div>
                    <span className="text-sm text-gray-700">
                      {treatment.title}
                    </span>
                    <span className="block text-3xl mt-2 font-medium">
                      {treatment.count}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

const DocConsultationPage = ({ consultations }) => {
  const navigate = useNavigate();
  const formatConsultationDate = ({ start, end }) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const options = { day: "2-digit", month: "short", year: "numeric" };
    const formattedDate = startDate.toLocaleDateString("en-US", options);
    const startTime = startDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    const endTime = endDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });

    return `${formattedDate} - ${startTime} to ${endTime}`;
  };

  const [toggleEndButton, setToggleEndButton] = useState(false)

  const { revalidate } = useRevalidator()

  return (
    <div className="">
      <h2 className="text-lg font-medium mb-4">Consultations</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-x-3 gap-y-4">
        {consultations.map((consultation) => (
          <ConsultationCard consultation={consultation} />
        ))}
      </div>
    </div>
  );
};

function ConsultationCard({ consultation }) {

  const navigate = useNavigate();
  const formatConsultationDate = ({ start, end }) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const options = { day: "2-digit", month: "short", year: "numeric" };
    const formattedDate = startDate.toLocaleDateString("en-US", options);
    const startTime = startDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    const endTime = endDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });

    return `${formattedDate} - ${startTime} to ${endTime}`;
  };
  const [toggleEndButton, setToggleEndButton] = useState(false)

  const { revalidate } = useRevalidator()

  return (
    <div
      key={consultation.id}
      className="border border-gray-300 pt-4 px-5 rounded-lg cursor-pointer bg-white hover:shadow-md duration-300"
      onClick={() => {
        if (toggleEndButton) {
          return
        } else {
          navigate(`${consultation.id}`)
        }

      }}
    >
      <div className="flex items-center justify-between border-b pb-3 gap-x-5">
        <div className="flex gap-x-3 items-center">
          <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center border">
            <Tag size={15} />
          </div>
          <span className="font-medium">#{consultation.id}</span>
        </div>
        {consultation.subscription?.status === "canceled" ? (
          <div
            className={`border px-2 py-1 rounded-md font-medium text-xs capitalize bg-red-100 border-red-600 text-orange-700`}
          >
            Canceled
          </div>
        ) : (
          <div className="relative">
            <div
              className={`border px-2 py-1 rounded-md font-medium text-xs capitalize ${consultation.meeting_status.toLowerCase() === "pending"
                  ? "bg-orange-100 border-orange-500 text-orange-700"
                  : "bg-green-100 border-green-500 text-green-700"
                }`}

              onMouseOver={() => {
                if (consultation.meeting_status.toLowerCase() === "pending") {
                  setToggleEndButton(true)
                } else {
                  return
                }
              }}
            >
              {consultation.meeting_status}
            </div>

            {
              toggleEndButton &&
              <CompleteToggleModal
                meeting_id={consultation.id}
                cursorOut={() => {
                  setToggleEndButton(false)
                }}
                complete={data => {
                  if (data) {
                    revalidate()
                    setToggleEndButton(false)
                  }
                }}
              />
            }

          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-2 gap-y-5 py-5">
        <div>
          <span className="text-gray-600 text-xs">Patient name:</span>
          <span className="font-medium block text-xs mt-0.5 break-words">
            {`${consultation.patient?.lastname} ${consultation.patient?.firstname}`}
          </span>
        </div>
        <div>
          <span className="text-gray-600 text-xs">Date booked:</span>
          <span className="font-medium block text-xs mt-0.5 break-words capitalize">
            {new Date(consultation.created_at).toDateString()}
          </span>
        </div>
        <div>
          <span className="text-gray-600 text-xs">Telephone:</span>
          <span className="font-medium block text-xs mt-0.5 break-words capitalize">
            {consultation.patient?.home_phone}
          </span>
        </div>
        <div>
          <span className="text-gray-600 text-xs">Email:</span>
          <span className="font-medium block text-xs mt-0.5 break-words">
            {consultation.patient?.email_address}
          </span>
        </div>
      </div>
      <div className="py-3 border-t">
        <div>
          <span className="text-gray-600 text-xs">
            Estimation schedule:
          </span>
          <span className="font-medium block text-xs mt-0.5 break-words">
            {formatConsultationDate(consultation)}
          </span>
        </div>
      </div>
    </div>
  )
}


function CompleteToggleModal({ meeting_id, complete, cursorOut }) {

  async function endConsultation() {
    instance.post("/telemedicine/meeting/end", { meeting_id }, {
      headers: {
        Authorization: `Bearer ${getToken()}`
      }
    })
      .then(response => {
        complete(true)
      })
      .catch(error => {
        toast.error(error.response.data.message)
      })
  }
  return (
    <>
      <div
        onMouseLeave={cursorOut}
        className="bg-white p-3 max-w-[200px] top-8 right-0 rounded-[10px] shadow-lg border absolute">
        <button onClick={endConsultation} className="bg-red-500 whitespace-nowrap text-white rounded-[6px] px-4 py-2 text-sm flex items-center gap-1 font-medium">
          <X size={16} />
          End consultation
        </button>
      </div>
    </>
  )
}